import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { PageHero } from "@/components/PageHero";
import { IMAGES } from "@/data/content";
import { BUSINESS } from "@/data/legal";
import { useLang } from "@/i18n/LanguageContext";
import { useSeo } from "@/hooks/useSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COPY = {
  en: {
    seo: { title: "Withdrawal | Women's Den", description: "Withdraw from an online purchase at Women's Den — your statutory cooling-off right, exercised simply." },
    eyebrow: "Legal",
    title: "Withdrawal",
    intro: "Bought something online and want to withdraw? You can do that here — no account, and no reason needed.",
    coolingTitle: "Your cooling-off rights, in short",
    cooling: [
      "When you purchase a service online from Women's Den — such as the Menopause 1:1 four-month programme — you generally have a 14-day cooling-off period from confirmation of the service contract.",
      "If you ask Women's Den to begin providing the service during that period, your explicit agreement may be required.",
      "If you later withdraw after asking for early performance, payment may be due for the part of the service already provided.",
      "If the service has been fully performed during the cooling-off period, the right may end where the legally required consent and acknowledgement have been obtained.",
    ],
    scope: "A note on workshops and circles: tickets for an event on a specific date fall under different rules for services booked for an agreed date or period, so the cooling-off information above does not apply to them as a blanket rule. The cancellation terms for those bookings are shown with the booking form on each offering page.",
    formTitle: "Withdraw from an online purchase",
    name: "Your name",
    email: "Email used for the purchase",
    reference: "Booking or order reference (if you have it — optional)",
    service: "What did you purchase?",
    services: [
      "Menopause 1:1 Support — 4 Month Programme (€350)",
      "Active Birth Prenatal Workshop",
      "Birth Art for Pregnancy",
      "Mums & Babies Circle (6 weeks)",
      "Women's Circle",
      "Something else / not sure",
    ],
    noReason: "You don't need to give a reason. We only ask what is needed to identify your purchase.",
    review: "Review my withdrawal",
    reviewTitle: "Please check your withdrawal",
    confirm: "Confirm withdrawal",
    back: "Back",
    sending: "Sending…",
    doneHeading: "Your withdrawal has been recorded.",
    doneBody: (email, id) => `A confirmation email is on its way to ${email}, identifying the purchase and the date and time of your request (reference ${id}). Irida has been notified and will be in touch personally about anything that follows.`,
    error: "Something went wrong — please check the details and try again, or write to us directly.",
  },
  nl: {
    seo: { title: "Herroeping | Women's Den", description: "Herroep een online aankoop bij Women's Den — je wettelijke bedenktijd, eenvoudig uitgeoefend." },
    eyebrow: "Juridisch",
    title: "Herroeping",
    intro: "Online iets gekocht en wil je dat herroepen? Dat kan hier — zonder account en zonder opgaaf van reden.",
    coolingTitle: "Je wettelijke bedenktijd, kort samengevat",
    cooling: [
      "Wanneer je online een dienst van Women's Den afneemt — zoals het Menopauze 1-op-1 programma van vier maanden — heb je in beginsel een wettelijke bedenktijd van 14 dagen vanaf het sluiten van de overeenkomst.",
      "Vraag je Women's Den om de dienst al binnen die periode te beginnen, dan kan daarvoor je uitdrukkelijke toestemming nodig zijn.",
      "Herroep je daarna na een verzoek om vervroegde uitvoering, dan kan een betaling verschuldigd zijn voor het deel van de dienst dat al is geleverd.",
      "Is de dienst binnen de bedenktijd volledig geleverd, dan kan het recht vervallen wanneer de wettelijk vereiste toestemming en erkenning zijn gegeven.",
    ],
    scope: "Een kanttekening bij workshops en cirkels: kaartjes voor een bijeenkomst op een specifieke datum vallen onder andere regels voor diensten die zijn geboekt voor een overeengekomen datum of periode. De informatie hierboven geldt daarvoor dus niet zomaar. De annuleringsvoorwaarden voor die boekingen staan bij het boekingsformulier op elke aanbodpagina.",
    formTitle: "Herroep een online aankoop",
    name: "Je naam",
    email: "E-mailadres waarmee je hebt gekocht",
    reference: "Boekings- of orderreferentie (als je die hebt — niet verplicht)",
    service: "Wat heb je gekocht?",
    services: [
      "Menopauze 1-op-1 Begeleiding — programma van 4 maanden (€350)",
      "Active Birth Prenatal Workshop",
      "Birth Art for Pregnancy",
      "Mums & Babies Circle (6 weken)",
      "Women's Circle",
      "Iets anders / weet ik niet",
    ],
    noReason: "Je hoeft geen reden te geven. We vragen alleen wat nodig is om je aankoop te vinden.",
    review: "Controleer mijn herroeping",
    reviewTitle: "Controleer je herroeping",
    confirm: "Herroeping bevestigen",
    back: "Terug",
    sending: "Versturen…",
    doneHeading: "Je herroeping is vastgelegd.",
    doneBody: (email, id) => `Er is een bevestigingsmail onderweg naar ${email} met de aankoop en de datum en tijd van je verzoek (referentie ${id}). Irida is op de hoogte gesteld en neemt persoonlijk contact op over wat er eventueel volgt.`,
    error: "Er ging iets mis — controleer de gegevens en probeer het opnieuw, of schrijf ons direct.",
  },
};

const inputCls =
  "w-full bg-transparent border-b border-ink/25 py-3 text-base text-ink outline-none placeholder:text-soft/50 focus:border-clay transition-colors duration-300";
const labelCls = "text-xs tracking-[0.25em] uppercase text-soft";

export default function Withdrawal() {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.en;
  useSeo({ title: c.seo.title, description: c.seo.description });
  const [form, setForm] = useState({ name: "", email: "", reference: "", service: c.services[0] });
  const [stage, setStage] = useState("form"); // form | review | done
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [resultId, setResultId] = useState(null);
  const [website, setWebsite] = useState(""); // honeypot

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    setSending(true);
    setError(null);
    try {
      const res = await fetch(`${API}/withdrawals`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, reference: form.reference || undefined, lang, website }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setResultId(data.id);
      setStage("done");
    } catch {
      setError(c.error);
      setStage("form");
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="bg-cream" data-testid="withdrawal-page">
      <PageHero eyebrow={c.eyebrow} title={c.title} image={IMAGES.botanical} testid="withdrawal-hero" />
      <section className="mx-auto max-w-3xl px-6 pb-28">
        <Reveal>
          <p className="font-serif italic text-2xl leading-snug text-clay" data-testid="withdrawal-intro">{c.intro}</p>

          <h2 className="mt-14 font-serif text-2xl tracking-tight text-ink" data-testid="withdrawal-cooling-title">{c.coolingTitle}</h2>
          <div className="mt-6 space-y-4">
            {c.cooling.map((p, i) => (
              <p key={i} className="text-base leading-relaxed text-soft" data-testid={`withdrawal-cooling-${i}`}>{p}</p>
            ))}
          </div>
          <p className="mt-6 text-sm leading-relaxed text-soft/80 border-l-2 border-sand pl-5" data-testid="withdrawal-scope">{c.scope}</p>

          <h2 className="mt-16 font-serif text-2xl tracking-tight text-ink" data-testid="withdrawal-form-title">{c.formTitle}</h2>

          {stage === "form" && (
            <div className="mt-8 space-y-7" data-testid="withdrawal-form">
              <div>
                <label htmlFor="wd-name" className={labelCls}>{c.name}</label>
                <input id="wd-name" value={form.name} onChange={set("name")} className={inputCls} data-testid="withdrawal-name" />
              </div>
              <div>
                <label htmlFor="wd-email" className={labelCls}>{c.email}</label>
                <input id="wd-email" type="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="withdrawal-email" />
              </div>
              <div>
                <label htmlFor="wd-ref" className={labelCls}>{c.reference}</label>
                <input id="wd-ref" value={form.reference} onChange={set("reference")} className={inputCls} data-testid="withdrawal-reference" />
              </div>
              <div>
                <label htmlFor="wd-service" className={labelCls}>{c.service}</label>
                <select id="wd-service" value={form.service} onChange={set("service")} data-testid="withdrawal-service"
                  className="mt-1 w-full bg-transparent border-b border-ink/25 py-3 text-base text-ink outline-none focus:border-clay transition-colors duration-300">
                  {c.services.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <input type="text" value={website} onChange={(e) => setWebsite(e.target.value)} tabIndex={-1} autoComplete="off"
                className="hidden" aria-hidden="true" data-testid="withdrawal-website-honeypot" />
              <p className="text-sm text-soft/80" data-testid="withdrawal-noreason">{c.noReason}</p>
              {error && <p className="text-sm text-rust" data-testid="withdrawal-error">{error}</p>}
              <button type="button" disabled={!form.name.trim() || !form.email.trim()}
                onClick={() => setStage("review")} data-testid="withdrawal-review-btn"
                className="group inline-flex items-center gap-3 rounded-full bg-clay px-8 py-4 text-sm tracking-wide text-cream transition-colors duration-300 hover:bg-rust disabled:opacity-50 disabled:cursor-not-allowed">
                {c.review}
                <ArrowRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-0.5" />
              </button>
            </div>
          )}

          {stage === "review" && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-8" data-testid="withdrawal-review">
              <p className="text-xs tracking-[0.25em] uppercase text-soft mb-4">{c.reviewTitle}</p>
              <dl className="border border-ink/10 p-6 space-y-3 text-sm">
                <div className="flex gap-4"><dt className="w-40 shrink-0 text-soft">{c.name}</dt><dd className="text-ink" data-testid="review-name">{form.name}</dd></div>
                <div className="flex gap-4"><dt className="w-40 shrink-0 text-soft">{c.email}</dt><dd className="text-ink" data-testid="review-email">{form.email}</dd></div>
                {form.reference && <div className="flex gap-4"><dt className="w-40 shrink-0 text-soft">{c.reference.split(" (")[0]}</dt><dd className="text-ink" data-testid="review-reference">{form.reference}</dd></div>}
                <div className="flex gap-4"><dt className="w-40 shrink-0 text-soft">{c.service}</dt><dd className="text-ink" data-testid="review-service">{form.service}</dd></div>
              </dl>
              {error && <p className="mt-4 text-sm text-rust" data-testid="withdrawal-error">{error}</p>}
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <button type="button" onClick={submit} disabled={sending} data-testid="withdrawal-confirm-btn"
                  className="group inline-flex items-center gap-3 rounded-full bg-clay px-8 py-4 text-sm tracking-wide text-cream transition-colors duration-300 hover:bg-rust disabled:opacity-60">
                  {sending ? c.sending : c.confirm}
                  {!sending && <Check size={16} strokeWidth={1.5} />}
                </button>
                <button type="button" onClick={() => setStage("form")} data-testid="withdrawal-back-btn"
                  className="text-sm text-soft hover:text-ink transition-colors duration-300 border-b border-ink/20 pb-0.5">
                  {c.back}
                </button>
              </div>
            </motion.div>
          )}

          {stage === "done" && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-8" data-testid="withdrawal-done">
              <p className="font-serif text-2xl tracking-tight text-ink" data-testid="withdrawal-done-heading">{c.doneHeading}</p>
              <p className="mt-4 text-base leading-relaxed text-soft" data-testid="withdrawal-done-body">{c.doneBody(form.email, resultId)}</p>
            </motion.div>
          )}

          <div className="mt-16 border-t border-ink/10 pt-8" data-testid="legal-business">
            <p className="text-xs tracking-[0.25em] uppercase text-soft mb-4">{BUSINESS.title[lang] || BUSINESS.title.en}</p>
            <address className="not-italic text-sm leading-relaxed text-soft">
              {BUSINESS.lines.map((l) => <span key={l} className="block">{l}</span>)}
            </address>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
